<template>
  <div class="header-wrapper">
    <Banner />

    <div class="header">
      <div class="container">
        <div class="header__middle">
          <a href="/">
            <img src="@/assets/images/logo.svg" class="header__logo" />
          </a>
        </div>

        <div class="header__left">
          <IconButton
            class="header__icon u__hide--desktop"
            icon="burger"
            @click="toggleMobileMenu"
          />
          <IconButton
            tag="a"
            class="header__icon u__hide--desktop"
            icon="search"
            href="/search"
          />

          <nav class="navigation u__hide--mobile">
            <Button :transparent="true" @click="setNavOpen(true)">
              Shop
            </Button>
            <a class="navigation__item" href="/blogs/journal">Journal</a>
            <a href="/pages/members" class="navigation__item">Members Only.</a>
          </nav>
        </div>

        <div class="header__right">
          <IconButton
            class="header__icon u__hide--desktop"
            icon="bag"
            @click="setCartOpen(true)"
          />
          <nav class="navigation u__hide--mobile">
            <Button :transparent="true" @click="setSearchOpen(true)">
              Search
            </Button>
            <a href="/pages/help" class="navigation__item">Help</a>
            <template v-if="isLoggedIn">
              <a :href="accountUrl" class="navigation__item">Account</a>
            </template>
            <template v-else>
              <Button :transparent="true" @click="setAccountOpen(true)">
                Account
              </Button>
            </template>
            <Button :transparent="true" @click="setCartOpen(true)">
              Bag
            </Button>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <BurgerMenu v-if="isBurgerMenuOpen" />
  <Bag />
  <Search v-if="mq.mdPlus" />
  <DesktopLoginPanel v-if="mq.mdPlus" />
  <DesktopNavigation v-if="mq.mdPlus" />
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import Button from "@/components/Button.vue";
import IconButton from "@/components/IconButton.vue";
import Banner from "@/components/Banner.vue";
import { defineAsyncComponent } from "vue";

const Bag = defineAsyncComponent(() =>
  import(/* webpackChunkName: "Bag" */ "@/components/Bag.vue")
);
const Search = defineAsyncComponent(() =>
  import(
    /* webpackChunkName: "SearchDesktop" */ "@/components/SearchDesktop.vue"
  )
);
const DesktopLoginPanel = defineAsyncComponent(() =>
  import(
    /* webpackChunkName: "DesktopLoginPanel" */ "@/components/DesktopLoginPanel.vue"
  )
);
const DesktopNavigation = defineAsyncComponent(() =>
  import(
    /* webpackChunkName: "DesktopNavigation" */ "@/components/navigation/DesktopNavigation.vue"
  )
);
const BurgerMenu = defineAsyncComponent(() =>
  import(/* webpackChunkName: "BurgerMenu" */ "@/components/BurgerMenu.vue")
);

export default {
  name: "HeaderComponent",
  components: {
    Button,
    IconButton,
    Bag,
    BurgerMenu,
    DesktopLoginPanel,
    Banner,
    DesktopNavigation,
    Search,
  },
  inject: ["mq"],
  computed: {
    ...mapState("app", ["isBurgerMenuOpen"]),
    ...mapState("auth", ["multipassAccount"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    accountUrl() {
      return this.multipassAccount.url || "/account/login";
    },
  },
  methods: {
    ...mapActions("app", ["toggleMobileMenu", "setAccountOpen", "setNavOpen"]),
    ...mapActions("search", ["setSearchOpen"]),
    ...mapActions("cart", ["setCartOpen"]),
  },
};
</script>

<style lang="scss" scoped>
.header {
  background-color: $color-white;
  height: 48px;

  & > .container {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 20px;
  }

  @include respond-above(md) {
    position: relative;
    height: auto;
  }

  &__middle {
    flex: 1 0 auto;
    order: 1;
    text-align: center;
  }

  &__left,
  &__right {
    flex: 0 1 auto;

    @include respond-above(md) {
      flex: 1 1 0;
    }
  }

  &__left {
    order: 0;
  }

  &__right {
    order: 2;
    text-align: right;

    @include respond-above(md) {
      display: flex;
      justify-content: flex-end;
      -webkit-justify-content: flex-end;
    }
  }

  &__icon {
    font-size: 18px;
  }

  &__logo {
    vertical-align: top;
    padding: 15px 0;
  }
}

.navigation {
  @include respond-above(md) {
    display: flex;
  }
  gap: 20px;
  align-items: center;

  &__item {
    text-transform: uppercase;
    font-size: 9px;
    font-weight: 600;
    text-decoration: none;
    color: $color-dark;
    outline: none;
    border: none;
    background-color: transparent;

    @include respond-above(md) {
      font-size: 11px;
    }
  }

  ::v-deep(button) {
    font-size: 11px;
  }
}

.template-index .header {
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0) 100%
  );

  * {
    transition: 0.2s;
  }

  *,
  ::v-deep(button) {
    color: $color-white !important;
  }

  .header__logo {
    filter: invert(1);
  }
}
</style>

<style lang="scss">
.fixed-header {
  padding-top: 83px;

  &.template-index {
    padding-top: 34px;

    @include respond-above(md) {
      padding-top: 36px;
    }
  }

  .header-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 4;
  }
}

.header__left {
  .icon-button:first-child {
    margin-left: -15px;
  }
}

.header__right {
  .icon-button:first-child {
    margin-right: -15px;
  }
}
</style>
